import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Mail, Send, Loader2, CheckCircle2, XCircle, RefreshCw,
  KeyRound, UserPlus, ShieldCheck, Gift, Ticket, Inbox,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// The 5 production flows the user asked us to verify.  registration_admin and
// registration_user are split out separately because the registration step
// fires two distinct emails (admin notification + user acknowledgement).
const FLOWS = [
  {
    key: 'password_reset',
    label: 'Password Reset',
    icon: KeyRound,
    color: '#004ac6',
    bg: '#eff4ff',
    description: 'Mimics POST /auth/forgot-password. Should arrive from support@.',
    senderEnv: 'SENDER_EMAIL',
  },
  {
    key: 'registration_user',
    label: 'New Registration · User Welcome',
    icon: UserPlus,
    color: '#0891b2',
    bg: '#ecfeff',
    description: 'User-facing acknowledgement after sign-up (pending-approval notice).',
    senderEnv: 'SENDER_EMAIL',
  },
  {
    key: 'registration_admin',
    label: 'New Registration · Admin Notification',
    icon: Inbox,
    color: '#0b1c30',
    bg: '#f1f5f9',
    description: 'Notification sent to ADMIN_EMAIL with the new user details.',
    senderEnv: 'SENDER_EMAIL',
  },
  {
    key: 'registration_approval',
    label: 'Registration Approval',
    icon: ShieldCheck,
    color: '#16a34a',
    bg: '#ecfdf5',
    description: 'Activation email after admin approves a pending account.',
    senderEnv: 'SENDER_EMAIL',
  },
  {
    key: 'gift',
    label: 'Gift Reports',
    icon: Gift,
    color: '#16a34a',
    bg: '#ecfdf5',
    description: 'Admin grants complimentary report credits. From gift@.',
    senderEnv: 'SENDER_EMAIL_GIFT',
  },
  {
    key: 'coupon',
    label: 'Coupon Redeemed',
    icon: Ticket,
    color: '#7c3aed',
    bg: '#f5f3ff',
    description: 'User redeemed a coupon code. From coupon@.',
    senderEnv: 'SENDER_EMAIL_COUPON',
  },
];

function StatusPill({ result, loading }) {
  if (loading) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-blue-50 text-blue-700">
        <Loader2 className="w-3 h-3 animate-spin" /> Sending…
      </span>
    );
  }
  if (!result) {
    return (
      <span className="text-[11px] font-bold uppercase tracking-widest text-[#737686]">— not tested —</span>
    );
  }
  if (result.success) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-emerald-50 text-emerald-700">
        <CheckCircle2 className="w-3 h-3" /> Sent
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-rose-50 text-rose-700">
      <XCircle className="w-3 h-3" /> Failed
    </span>
  );
}

export default function AdminEmailTestPage() {
  const [recipient, setRecipient] = useState(localStorage.getItem('email_test_recipient') || '');
  const [fullName, setFullName] = useState('Sovereign IQ Tester');
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagLoading, setDiagLoading] = useState(true);
  const [results, setResults] = useState({});      // { flow: { success, ... } }
  const [pending, setPending] = useState({});      // { flow: true } while sending
  const [batchRunning, setBatchRunning] = useState(false);

  const loadDiagnostics = useCallback(async () => {
    setDiagLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/email-diagnostics`, { headers: auth() });
      setDiagnostics(data);
    } catch (e) {
      toast.error('Failed to load email diagnostics');
    } finally {
      setDiagLoading(false);
    }
  }, []);

  useEffect(() => { loadDiagnostics(); }, [loadDiagnostics]);

  // Persist recipient between visits — admins typically retest with same address.
  useEffect(() => {
    if (recipient) localStorage.setItem('email_test_recipient', recipient);
  }, [recipient]);

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || '').trim());

  const sendOne = useCallback(async (flow) => {
    if (!isValidEmail(recipient)) {
      toast.error('Enter a valid recipient email first');
      return null;
    }
    setPending((p) => ({ ...p, [flow]: true }));
    try {
      const { data } = await axios.post(
        `${API}/admin/email-test/${flow}`,
        { to: recipient.trim(), full_name: fullName.trim() || 'Sovereign IQ Tester' },
        { headers: auth() },
      );
      setResults((r) => ({ ...r, [flow]: data }));
      if (data.success) {
        toast.success(`${flow.replace(/_/g, ' ')} sent ✓`);
      } else {
        toast.error(`${flow}: ${data.reason || 'send failed'}`);
      }
      return data;
    } catch (e) {
      const reason = e?.response?.data?.detail || e.message || 'Unknown error';
      const failed = { success: false, reason, flow };
      setResults((r) => ({ ...r, [flow]: failed }));
      toast.error(`${flow}: ${reason}`);
      return failed;
    } finally {
      setPending((p) => ({ ...p, [flow]: false }));
    }
  }, [recipient, fullName]);

  const sendAll = useCallback(async () => {
    if (!isValidEmail(recipient)) {
      toast.error('Enter a valid recipient email first');
      return;
    }
    setBatchRunning(true);
    setResults({});
    // Sequential to avoid Resend rate-limit spikes.
    for (const f of FLOWS) {
      // eslint-disable-next-line no-await-in-loop
      await sendOne(f.key);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res) => setTimeout(res, 600));
    }
    setBatchRunning(false);
    toast.success('Batch test complete — check your inbox + spam folder');
  }, [recipient, sendOne]);

  const okCount = FLOWS.filter((f) => results[f.key]?.success).length;
  const failCount = FLOWS.filter((f) => results[f.key] && !results[f.key].success).length;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-email-test-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold text-[#0b1c30] tracking-tight flex items-center gap-2">
            <Mail className="w-6 h-6 text-[#004ac6]" />
            Email Diagnostics
          </h1>
          <p className="text-sm text-[#434655] mt-1 max-w-2xl">
            Fire test copies of all 5 production email flows to verify deliverability, sender domain, and template rendering.
            Each subject is prefixed with <code className="bg-[#f1f5f9] px-1 rounded">[TEST]</code> so recipients know it's a diagnostic.
          </p>
        </div>
        <button
          type="button"
          onClick={loadDiagnostics}
          className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#004ac6] hover:bg-[#eff4ff] px-3 py-1.5 rounded-lg"
          data-testid="email-test-reload-diagnostics"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${diagLoading ? 'animate-spin' : ''}`} />
          Reload config
        </button>
      </div>

      {/* Config snapshot */}
      <div className="bg-white rounded-xl border border-[#c3c6d7]/30 p-5 shadow-sm">
        <div className="text-[10px] font-black uppercase tracking-widest text-[#737686] mb-3">Live Configuration</div>
        {diagLoading ? (
          <div className="flex items-center gap-2 text-sm text-[#737686]"><Loader2 className="w-4 h-4 animate-spin" /> loading…</div>
        ) : !diagnostics ? (
          <div className="text-sm text-rose-600">Failed to load</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm">
            <ConfigRow label="Resend API key" value={diagnostics.resend_api_configured ? `${diagnostics.resend_api_key_preview} ✓` : '— missing —'} ok={diagnostics.resend_api_configured} />
            <ConfigRow label="Verified domain" value={diagnostics.verified_domain?.domain ? `${diagnostics.verified_domain.domain} (${diagnostics.verified_domain.status || 'unknown'})` : '— none —'} ok={!!diagnostics.verified_domain?.verified} />
            <ConfigRow label="Sender (platform)" value={diagnostics.sender_email || '— missing —'} ok={!!diagnostics.sender_email} />
            <ConfigRow label="Sender (gift)" value={diagnostics.sender_email_gift || '— missing —'} ok={!!diagnostics.sender_email_gift} />
            <ConfigRow label="Sender (coupon)" value={diagnostics.sender_email_coupon || '— missing —'} ok={!!diagnostics.sender_email_coupon} />
            <ConfigRow label="BCC admin" value={diagnostics.admin_email_bcc || '— missing —'} ok={!!diagnostics.admin_email_bcc} />
          </div>
        )}
      </div>

      {/* Recipient input + batch */}
      <div className="bg-gradient-to-br from-white to-[#f8f9ff] rounded-xl border border-[#c3c6d7]/30 p-5 shadow-sm">
        <div className="grid md:grid-cols-3 gap-3">
          <label className="md:col-span-2 block">
            <span className="text-[10px] font-black uppercase tracking-widest text-[#737686]">Recipient email</span>
            <input
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="you@yourdomain.com"
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[#c3c6d7]/50 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#004ac6]/30 focus:border-[#004ac6]"
              data-testid="email-test-recipient-input"
            />
          </label>
          <label className="block">
            <span className="text-[10px] font-black uppercase tracking-widest text-[#737686]">Full name (template variable)</span>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Sovereign IQ Tester"
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[#c3c6d7]/50 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#004ac6]/30 focus:border-[#004ac6]"
              data-testid="email-test-fullname-input"
            />
          </label>
        </div>
        <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
          <div className="text-xs text-[#737686]">
            {okCount > 0 || failCount > 0 ? (
              <span>
                <strong className="text-emerald-700">{okCount} sent</strong> · <strong className="text-rose-700">{failCount} failed</strong> · <strong>{FLOWS.length - okCount - failCount} pending</strong>
              </span>
            ) : (
              <span>No tests run yet for this session.</span>
            )}
          </div>
          <button
            type="button"
            onClick={sendAll}
            disabled={batchRunning || !isValidEmail(recipient)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0b1c30] hover:bg-[#04122c] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold tracking-tight transition"
            data-testid="email-test-send-all"
          >
            {batchRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {batchRunning ? 'Sending all 6…' : 'Send all 6 test emails'}
          </button>
        </div>
      </div>

      {/* Per-flow cards */}
      <div className="grid md:grid-cols-2 gap-4">
        {FLOWS.map((flow) => {
          const Icon = flow.icon;
          const result = results[flow.key];
          const isPending = !!pending[flow.key];
          return (
            <div
              key={flow.key}
              className="bg-white rounded-xl border border-[#c3c6d7]/30 p-5 shadow-sm hover:shadow-md transition"
              data-testid={`email-test-card-${flow.key}`}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: flow.bg }}>
                  <Icon className="w-5 h-5" style={{ color: flow.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <h3 className="text-sm font-extrabold text-[#0b1c30] tracking-tight">{flow.label}</h3>
                    <StatusPill result={result} loading={isPending} />
                  </div>
                  <p className="text-xs text-[#434655] mt-1">{flow.description}</p>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#737686] mt-1.5">
                    Uses: <span className="text-[#004ac6]">{flow.senderEnv}</span>
                  </div>
                </div>
              </div>

              {/* Result detail */}
              {result && (
                <div className={`mt-2 p-3 rounded-lg text-xs font-mono leading-relaxed ${result.success ? 'bg-emerald-50 text-emerald-900 border border-emerald-200' : 'bg-rose-50 text-rose-900 border border-rose-200'}`}>
                  {result.success ? (
                    <>
                      <div><strong>From:</strong> {result.sender}</div>
                      <div><strong>To:</strong> {result.recipient}</div>
                      <div className="break-all"><strong>Resend ID:</strong> {result.resend_id || '—'}</div>
                    </>
                  ) : (
                    <>
                      <div><strong>Failure reason:</strong></div>
                      <div className="mt-1 break-all">{result.reason || 'unknown'}</div>
                    </>
                  )}
                </div>
              )}

              <button
                type="button"
                onClick={() => sendOne(flow.key)}
                disabled={isPending || batchRunning || !isValidEmail(recipient)}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#004ac6] hover:bg-[#003a9a] disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold uppercase tracking-widest transition"
                data-testid={`email-test-send-${flow.key}`}
              >
                {isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {isPending ? 'Sending…' : 'Send test'}
              </button>
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-[#737686] leading-relaxed">
        Tip: A successful API response only confirms Resend accepted the request. Always check the recipient inbox <em>and</em> spam folder to confirm actual delivery.
        If the verified-domain badge above shows <strong>not verified</strong>, sender-domain mismatches will be blocked at the API layer (returned with reason <code>sender_domain_mismatch</code>).
      </p>
    </div>
  );
}

function ConfigRow({ label, value, ok }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1 border-b border-[#f1f5f9] last:border-0">
      <span className="text-[10px] font-black uppercase tracking-widest text-[#737686] shrink-0">{label}</span>
      <span className={`text-xs font-mono truncate ${ok ? 'text-[#0b1c30]' : 'text-rose-600'}`} title={value}>{value}</span>
    </div>
  );
}
